/*
 * VatomInc. Copyright (c) 2022, all rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in the root directory of this source tree.
 */

import { ProfileSnapshot, VatomInc } from '@vatom/sdk/core'
import { VatomApiType, VatomTokenType } from '@vatom/vatom/plugin'

export default class BridgeV2 {
  vatomIncApi: VatomInc.VatomIncApi
  api: VatomApiType
  vatom: VatomTokenType
  face: any
  version: number
  encodedVatom: any

  constructor(
    api: VatomApiType,
    vatomIncApi: VatomInc.VatomIncApi,
    vatom: VatomTokenType,
    face: any
  ) {
    this.api = api
    this.vatom = vatom
    this.vatomIncApi = vatomIncApi
    this.face = face
    this.encodedVatom = this.encodeVatom(this.vatom)
    this.version = 2
  }

  init() {
    return {
      vatom: this.encodedVatom,
      face: this.face
    }
  }

  // TODO
  // getVatom() {
  // 	return this.vatomNet.Vatoms.getUserVatoms([this.vatom.id]).then((v) => {
  // 		return {
  // 			vatom: this.encodeVatom(v[0]),
  // 		};
  // 	});
  // }

  getVatomChildren() {
    const vatoms = this.vatom.getChildren() as VatomTokenType[]

    return {
      vatoms: vatoms.map(v => ({
        ...v,
        actions: v.vatomActions,
        'vAtom::vAtomType': v.properties
      }))
    }
  }

  vatomParentSet(payload: any) {
    // @ts-expect-error get should be existing in the region not sure why this is giving an error
    const token = this.vatom.region.get(payload.id) as VatomTokenType
    token.setParentId(payload.parent_id)
  }

  // TODO
  // observeChildren(payload) {
  // 	let vId = payload;
  // 	return this.vatom.observeChildren(payload.id).then((v) => {
  // 		return {
  // 			id: vId,
  // 			vatoms: v,
  // 		};
  // 	});
  // }

  performAction(payload: { action_name: string; payload: any }) {
    if (this.vatom.id === payload.payload['this.id']) {
      return this.vatom.performAction(payload.action_name, payload.payload)
    }
  }

  getUserProfile(payload: any) {
    return this.vatomIncApi.getPublicProfile(this.vatom.properties.owner).then(u => {
      return this.encodeUser(u)
    })
  }

  getCurrentUser(payload: any) {
    // TODO: this is crashing, check the implementation of the APIs
    return Promise.all([
      this.vatomIncApi.getPublicProfile(this.vatom.properties.owner),
      this.vatomIncApi.getIdentities().catch(e => {
        console.error('BridgeV2.getCurrentUser error', e)
        throw e
      })
    ]).then(data => {
      const user = data[0]
      const tokens = data[1]

      if (!user) return {}

      return {
        user: {
          id: user.id,
          properties: {
            avatar_uri: user.picture,
            first_name: user.name.split(' ')[0],
            last_name: user.name.split(' ')[1],
            is_guest: false
          },
          tokens: {
            has_email: tokens.some(t => t.type === 'email'),
            has_phone: tokens.some(t => t.type === 'phoneNumber'),
            has_verified_email: tokens.some(t => t.verified && t.type === 'email'),
            has_verified_phone: tokens.some(t => t.verified && t.type === 'phoneNumber')
          }
        }
      }
    })
  }

  encodeResource(res: any) {
    const encodedUrls = []
    for (const u of res.urls) {
      const eur = this.api.vatomApi.encodeAssetProvider(u)
      encodedUrls.push(eur)
    }

    return {
      urls: encodedUrls
    }
  }

  updateVatom(vatom: VatomTokenType) {
    this.encodedVatom = vatom
  }

  encodeVatom(vatom: VatomTokenType): any {
    return {
      ...vatom.payload,
      faces: vatom.vatomFaces,
      actions: vatom.vatomActions
    }
  }

  customMessage(payload: any) {
    return payload
  }

  encodeUser(user: ProfileSnapshot | undefined) {
    if (!user) return
    return {
      user: {
        id: user.id,
        properties: {
          avatar_uri: user.picture,
          first_name: user.name.split(' ')[0],
          last_name: user.name.split(' ')[1]
        }
      }
    }
  }

  /** Fetches information about the inventory */
  inventoryStats(payload: any) {
    // Get variations
    let template_variations = payload.template_variations || []

    // Ensure only template variations with the same FQDN as this vatom are allowed
    template_variations = template_variations.filter((tv: string) =>
      tv.startsWith(this.vatom.properties.publisher_fqdn + ':')
    )

    // Call it
    return this.api.vatomApi.client.request(
      'POST',
      '/v1/user/vatom/inventorystats',
      { template_variations },
      true
    )
  }
}
